import { Injectable, inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { CanActivate, ActivatedRouteSnapshot } from '@angular/router';
import { Observable, of, race, timer } from 'rxjs';
import { map, filter, take, switchMap } from 'rxjs/operators';
import { FeatureFlagService } from './feature-flag.service';
import { UserService } from '../userService';

/**
 * How long to wait for the user to resolve before judging a role-gated flag without one.
 * Bounded on purpose: a logged-out visitor never resolves a user, and an unbounded wait would
 * hang navigation instead of redirecting them.
 */
const USER_RESOLVE_TIMEOUT_MS = 5000;

@Injectable({ providedIn: 'root' })
export class FeatureFlagGuard implements CanActivate {
    private featureFlagService = inject(FeatureFlagService);
    private userService = inject(UserService);

    /** Built here rather than per-navigation: `toObservable` requires an injection context. */
    private user$ = toObservable(this.userService.user);

    canActivate(route: ActivatedRouteSnapshot): Observable<boolean> {
        const feature = route.data['requiredFeature'] as string;
        if (!feature) {
            return of(true);
        }
        return this.featureFlagService.flags$.pipe(
            filter((flags) => flags.length > 0),
            take(1),
            switchMap((flags) => {
                const flag = flags.find((f) => f.feature === feature);

                // Only a role-gated flag depends on the user. Everything else is answerable from
                // the flags alone, so don't make those navigations wait.
                if (!flag?.requiredRoles?.length) {
                    return of(true);
                }

                // `isFeatureVisible` reads `userService.user()`, which is populated
                // asynchronously from settings. Guards on the same route run concurrently, so on
                // a cold load -- a deep link, or a refresh -- the flags can arrive first and this
                // guard then judges the user role-less and denies a feature they can actually
                // see. The route is cancelled and the app silently falls back to `/`, which is
                // indistinguishable from a broken link. Waiting for the user closes that race.
                return race(
                    this.user$.pipe(
                        filter((user) => !!user),
                        take(1)
                    ),
                    timer(USER_RESOLVE_TIMEOUT_MS)
                );
            }),
            take(1),
            map(() => this.featureFlagService.isFeatureVisible(feature)())
        );
    }
}
